import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SEOMeta from "../components/SEOMeta";
import { Reveal, MaskedLine } from "../components/Reveal";
import { useContent } from "../lib/content";

export default function Approach() {
  const content = useContent();

  return (
    <div>
      <SEOMeta
        title="Our Approach | Touson Ventures (OPC) Private Limited"
        description="How Touson Ventures identifies opportunities, understands customers and builds sustainable businesses with technology, innovation and disciplined execution."
      />

      <section className="pt-44 pb-16 sm:pb-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(212,175,55,0.06),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 1 }}
            className="font-mono text-[10px] sm:text-xs tracking-[0.35em] text-gold mb-8"
            data-testid="approach-eyebrow"
          >
            OUR APPROACH
          </motion.p>
          <h1 className="font-display tracking-tight text-white text-4xl sm:text-6xl lg:text-7xl leading-[1.05] max-w-4xl" data-testid="approach-headline">
            <MaskedLine delay={0.15}>Disciplined thinking.</MaskedLine>
            <MaskedLine delay={0.32} className="gold-gradient-text italic">Deliberate building.</MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 max-w-2xl text-white/65 text-base sm:text-lg leading-relaxed"
            data-testid="approach-intro"
          >
            {content.approach_page.intro}
          </motion.p>
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10" data-testid="approach-steps">
          {content.approach_page.steps.map((step, i) => (
            <Reveal key={step.title} delay={0.03 * i}>
              <div
                data-testid={`approach-step-${i}`}
                className="group grid md:grid-cols-12 gap-3 md:gap-8 py-9 sm:py-11 border-t border-white/10 last:border-b items-baseline hover:bg-white/[0.02] transition-colors duration-500 px-2 sm:px-4"
              >
                <span className="md:col-span-2 font-mono text-lg text-gold/80 group-hover:text-gold transition-colors duration-300">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="md:col-span-4 font-display text-xl sm:text-2xl text-white group-hover:text-gold-light transition-colors duration-300">
                  {step.title}
                </h2>
                <p className="md:col-span-6 text-sm sm:text-base text-white/55 leading-relaxed">{step.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal>
            <div className="border border-white/10 p-10 sm:p-16 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.07),transparent_60%)]" />
              <div className="relative">
                <h2 className="font-display text-3xl sm:text-4xl text-white tracking-tight">Have an opportunity in mind?</h2>
                <p className="mt-4 text-white/55 max-w-lg mx-auto">
                  We are open to exploring and developing opportunities across promising sectors.
                </p>
                <Link
                  to="/contact"
                  data-testid="approach-cta-button"
                  className="group mt-8 inline-flex items-center gap-3 bg-gold text-navy font-medium text-sm px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-300"
                >
                  Get in Touch
                  <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
